import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Activity, AlertCircle } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import useHubkomUsage from '../hooks/useHubkomUsage';
import EmptyState from '../components/common/EmptyState';

const ProgressBar = ({ used, limit, label }) => {
  const rawPercent = limit > 0 ? (used / limit) * 100 : 0;
  const overLimit = used > limit;
  const color = overLimit || rawPercent > 90
    ? 'from-red-500 to-red-600'
    : rawPercent > 75
      ? 'from-yellow-500 to-yellow-600'
      : 'from-[#3AD0E6] to-[#3630A4]';

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-400">{label}</span>
        <span className={`text-sm ${overLimit ? 'text-red-400 font-medium' : 'text-gray-400'}`}>
          {rawPercent.toFixed(1)}%
        </span>
      </div>
      <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
        <div
          className={`bg-gradient-to-r ${color} h-3 rounded-full transition-all`}
          style={{ width: `${Math.min(rawPercent, 100)}%` }}
        />
      </div>
    </div>
  );
};

const OverageToggle = ({ label, enabled, onToggle, rate, unit, isToggling }) => (
  <div className="flex items-center justify-between py-3 border-t border-gray-700">
    <div>
      <p className="text-sm text-white">{label}</p>
      {enabled && rate > 0 && (
        <p className="text-xs text-gray-400 mt-1">
          ${rate.toFixed(2)}/{unit} for usage over limit
        </p>
      )}
    </div>
    <button
      onClick={onToggle}
      disabled={isToggling}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
        enabled ? 'bg-[#3AD0E6]' : 'bg-gray-600'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        enabled ? 'translate-x-6' : 'translate-x-1'
      }`} />
    </button>
  </div>
);

const UsagePage = () => {
  const { selectedLocationId } = useSettings();
  const { data, isLoading, error, refetch, toggleOverage } = useHubkomUsage(selectedLocationId);
  const [togglingField, setTogglingField] = useState(null);
  const [toggleError, setToggleError] = useState(null);

  const handleToggle = async (field, value) => {
    setTogglingField(field);
    setToggleError(null);
    try {
      await toggleOverage(field, value);
    } catch {
      setToggleError('Failed to update overage setting.');
    } finally {
      setTogglingField(null);
    }
  };

  if (!selectedLocationId || selectedLocationId === 'all') {
    return (
      <div className="p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl text-white mb-2">Usage</h1>
            <p className="text-gray-400">Track your inbound and outbound usage</p>
          </div>
          <EmptyState
            icon={<Activity className="w-8 h-8 text-gray-400" />}
            title="Select a Location"
            description="Please select a specific location from the sidebar to view usage information."
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3AD0E6]" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl text-white mb-2">Usage</h1>
            <p className="text-gray-400">Track your inbound and outbound usage</p>
          </div>
          <Card className="bg-red-900/20 border-red-700 p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <p className="text-red-400">{error}</p>
            </div>
            <button
              onClick={refetch}
              className="mt-4 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </Card>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { billingCycle, inbound, outbound } = data;

  return (
    <div className="p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl text-white mb-2">Usage</h1>
          <p className="text-gray-400">Track your inbound and outbound usage</p>
        </div>

        {/* Billing Cycle */}
        {billingCycle && (
          <Card className="bg-gray-800/50 border-gray-700 p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-white font-medium">
                Billing Cycle: {billingCycle.start} — {billingCycle.end}
              </span>
              <span className="text-sm text-gray-400">
                {billingCycle.daysElapsed} of {billingCycle.daysTotal} days ({billingCycle.progressPercent.toFixed(1)}%)
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-1.5">
              <div
                className="bg-gray-500 h-1.5 rounded-full transition-all"
                style={{ width: `${billingCycle.progressPercent}%` }}
              />
            </div>
          </Card>
        )}

        {/* Limit Reached Banners */}
        {inbound.minutesUsed >= inbound.minutesLimit && !inbound.allowOverage && (
          <div className="mb-6 bg-red-900/30 border border-red-600 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-400">Inbound calls are disabled</p>
              <p className="text-xs text-red-400/70 mt-1">
                You've used all {inbound.minutesLimit} inbound minutes this cycle. Enable overage below to resume calls.
              </p>
            </div>
          </div>
        )}
        {outbound.dialsUsed >= outbound.dialsLimit && !outbound.allowOverage && (
          <div className="mb-6 bg-red-900/30 border border-red-600 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-400">Outbound dials are disabled</p>
              <p className="text-xs text-red-400/70 mt-1">
                You've used all {outbound.dialsLimit} outbound dials this cycle. Enable overage below to resume campaigns.
              </p>
            </div>
          </div>
        )}

        {/* Toggle Error */}
        {toggleError && (
          <div className="mb-6 bg-red-900/20 border border-red-700 rounded-lg p-3">
            <p className="text-sm text-red-400">{toggleError}</p>
          </div>
        )}

        {/* Usage Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Inbound Minutes */}
          <Card className="bg-gray-800/50 border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-white mb-6">Inbound Minutes</h2>

            <ProgressBar
              used={inbound.minutesUsed}
              limit={inbound.minutesLimit}
              label={`${inbound.minutesUsed.toFixed(1)} of ${inbound.minutesLimit} minutes used`}
            />

            <div className="mt-4 bg-gray-900/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Remaining</span>
                <span className="text-white font-semibold">
                  {Math.max(0, inbound.minutesLimit - inbound.minutesUsed).toFixed(1)} min
                </span>
              </div>
              {inbound.overageMinutes > 0 && (
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700">
                  <span className="text-red-400 text-sm">Overage</span>
                  <span className="text-red-400 font-semibold">
                    {inbound.overageMinutes.toFixed(1)} min (${inbound.overageCost.toFixed(2)})
                  </span>
                </div>
              )}
            </div>

            <OverageToggle
              label="Allow inbound overage"
              enabled={inbound.allowOverage}
              onToggle={() => handleToggle('allowInboundOverage', !inbound.allowOverage)}
              rate={inbound.overageRate}
              unit="min"
              isToggling={togglingField === 'allowInboundOverage'}
            />
          </Card>

          {/* Outbound Dials */}
          <Card className="bg-gray-800/50 border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-white mb-6">Outbound Dials</h2>

            <ProgressBar
              used={outbound.dialsUsed}
              limit={outbound.dialsLimit}
              label={`${outbound.dialsUsed} of ${outbound.dialsLimit} dials used`}
            />

            <div className="mt-4 bg-gray-900/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Remaining</span>
                <span className="text-white font-semibold">
                  {Math.max(0, outbound.dialsLimit - outbound.dialsUsed)} dials
                </span>
              </div>
              {outbound.overageDials > 0 && (
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700">
                  <span className="text-red-400 text-sm">Overage</span>
                  <span className="text-red-400 font-semibold">
                    {outbound.overageDials} dials (${outbound.overageCost.toFixed(2)})
                  </span>
                </div>
              )}
            </div>

            <OverageToggle
              label="Allow outbound overage"
              enabled={outbound.allowOverage}
              onToggle={() => handleToggle('allowOutboundOverage', !outbound.allowOverage)}
              rate={outbound.overageRate}
              unit="dial"
              isToggling={togglingField === 'allowOutboundOverage'}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UsagePage;
